import Modal from '@/components/modal/Modal';
import { Col } from '@/lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { MetricData, StageReviewInfo } from '@/features/centerReview';
import ModalAllReviewHeader from './ModalAllReviewHeader';
import ModalAllReviewContent from './ModalAllReviewContent';
import ModalAllReviewTable from './ModalAllReviewTable';

export type CodeValueType = {
    code: string;
    value: string;
};

type Props = {
    unmount?: () => void;
    stageData: StageReviewInfo;
    config: {
        key: string;
        label: string;
        badgeNumber: number;
    };
    metricData: MetricData[];
    initialSelection?: {
        questionCode?: string;
        questionSubject?: string;
        tab?: 'all' | 'positive' | 'negative' | 'custom';
        answerCode?: string;
        answerContent?: string;
    };
};

function ModalAllReview({ unmount, stageData, config, metricData, initialSelection }: Props) {
    const [selectedQuestionCode, setSelectedQuestionCode] = useState<CodeValueType>({
        code: initialSelection?.questionCode || stageData.stageQuestionRankings[0]?.stageQuestionCode || '',
        value: initialSelection?.questionSubject || stageData.stageQuestionRankings[0]?.stageQuestionSubject || '',
    });
    const [activeTab, setActiveTab] = useState<'all' | 'positive' | 'negative' | 'custom'>(
        initialSelection?.tab || 'all',
    );
    const [isExpanded, setIsExpanded] = useState(false);
    const [customFilter, setCustomFilter] = useState<'all' | 'positive' | 'negative'>('all');
    const [selectedAnswerCode, setSelectedAnswerCode] = useState<CodeValueType | null>(
        initialSelection?.answerCode && initialSelection?.answerContent
            ? {
                  code: initialSelection.answerCode,
                  value: initialSelection.answerContent,
              }
            : null,
    );
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleClose = () => {
        if (unmount) unmount();
    };

    // 선택된 질문 정보 찾기
    const selectedQuestion = useMemo(() => {
        return stageData.stageQuestionInfos.find((q) => q.questionCode === selectedQuestionCode.code);
    }, [stageData, selectedQuestionCode]);

    // 탭이 변경되면 확장 상태 초기화 (선택된 답변은 유지)
    useEffect(() => {
        setIsExpanded(false);
    }, [activeTab]);

    // 질문이 변경되면 선택된 답변 초기화
    // useEffect(() => {
    //     if (selectedAnswerCode) setSelectedAnswerCode(null);
    // }, [selectedQuestionCode.code]);

    if (!selectedQuestion) return null;

    return (
        <Modal onClose={handleClose} isCloseButton borderRadius={35} whiteViewHeight={windowHeight - 100}>
            <Col className="py-[42px] px-[50px] w-[1000px] h-[800px]">
                {/* 헤더 */}
                <ModalAllReviewHeader
                    badgeNumber={config.badgeNumber}
                    label={config.label}
                    stageData={stageData}
                    selectedQuestionCode={selectedQuestionCode}
                    onSelectQuestion={setSelectedQuestionCode}
                />

                {/* 컨텐츠 */}
                <ModalAllReviewContent
                    label={config.label}
                    selectedQuestion={selectedQuestion}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    customFilter={customFilter}
                    setCustomFilter={setCustomFilter}
                    selectedAnswerCode={selectedAnswerCode}
                    setSelectedAnswerCode={setSelectedAnswerCode}
                />

                {/* 테이블 */}
                <ModalAllReviewTable
                    metricData={metricData}
                    selectedQuestionCode={selectedQuestionCode}
                    label={config.label}
                    activeTab={activeTab}
                    selectedAnswerCode={selectedAnswerCode}
                />
            </Col>
        </Modal>
    );
}

export default ModalAllReview;
